/**
 * Email Verification Page
 * Visar status för email-verifiering
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';

const EmailVerification: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle(t('documentTitle.emailVerification'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorMessage(t('copy.admin.emailVerifyTokenMissing'));
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    try {
      const response = await api.get(`/api/auth/verify-email?token=${token}`);
      
      if (response.data.success) {
        setStatus('success');
        toast.success(t('copy.admin.emailVerifiedToast'));
        // Redirect till login efter 3 sekunder
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        throw new Error(response.data.error || t('copy.admin.emailVerifyFailed'));
      }
    } catch (error: any) {
      setStatus('error');
      setErrorMessage(error.response?.data?.error || error.message || t('copy.admin.emailVerifyError'));
      toast.error(error.response?.data?.error || t('copy.admin.emailVerifyError'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-100">
      <div className="max-w-md w-full bg-white dark:bg-white rounded-lg shadow-md p-8">
        {status === 'verifying' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-800 mb-2">{t('copy.admin.emailVerifyingTitle')}</h2>
            <p className="text-textSecondary dark:text-textSecondary">{t('copy.admin.emailVerifyingBody')}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-800 mb-2">{t('copy.admin.emailVerifiedTitle')}</h2>
            <p className="text-textSecondary dark:text-textSecondary mb-6">
              {t('copy.admin.emailVerifiedBody')}
            </p>
            <Link
              to="/login"
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('auth.goToLogin')}
            </Link>
            <p className="text-sm text-gray-600 mt-4">
              {t('copy.admin.emailVerifyRedirect')}
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-800 mb-2">{t('copy.admin.emailVerifyFailed')}</h2>
            <p className="text-textSecondary dark:text-textSecondary mb-6">{errorMessage}</p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-center"
              >
                {t('auth.goToLogin')}
              </Link>
              <p className="text-sm text-gray-600">
                {t('copy.admin.emailVerifyExpiredHint')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;

